import { maandVanIsoWeek } from '@/lib/locatieTypes';
import { isoWeekRange, isoWekenInJaar, isoWeekJaar } from '@/lib/locatieTypes';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getAccessToken } from '@/lib/tokenStore';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  TrendingUp, TrendingDown, Minus, Euro, Target, Users, ShoppingBag,
  Loader2, WifiOff, AlertTriangle, ChevronDown, CalendarDays
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERIODS = [
  { value: 'day', label: 'Dag' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Maand' },
  { value: 'quarter', label: 'Kwartaal' },
  { value: 'year', label: 'Jaar' },
];

const STATUS_CONFIG = {
  OK: { bg: 'bg-emerald-100', text: 'text-emerald-800', border: 'border-emerald-200', dot: 'bg-emerald-500' },
  'LET OP': { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-200', dot: 'bg-amber-500' },
  HOOG: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-200', dot: 'bg-red-500' },
  KRITIEK: { bg: 'bg-red-200', text: 'text-red-900', border: 'border-red-300', dot: 'bg-red-700' },
};

const STATUS_TOOLTIP = {
  OK: 'Alles binnen target',
  'LET OP': 'PK% boven target of bijverkoop onder target',
  HOOG: 'PK% boven 40% — directe actie nodig',
  KRITIEK: 'PK% boven 45% — kritieke situatie',
};

const MAAND_NAMEN = ['', 'Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni', 'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December'];

const formatCurrency = (v) => {
  if (!v && v !== 0) return '\u20AC 0';
  if (Math.abs(v) >= 1000000) return `\u20AC ${(v / 1000000).toFixed(2)}M`;
  if (Math.abs(v) >= 1000) return `\u20AC ${(v / 1000).toFixed(0)}K`;
  return `\u20AC ${Math.round(v).toLocaleString('nl-NL')}`;
};

function getISOWeek(d) {
  const dt = new Date(d.getTime());
  dt.setHours(0, 0, 0, 0);
  dt.setDate(dt.getDate() + 3 - ((dt.getDay() + 6) % 7));
  const w1 = new Date(dt.getFullYear(), 0, 4);
  return 1 + Math.round(((dt.getTime() - w1.getTime()) / 86400000 - 3 + ((w1.getDay() + 6) % 7)) / 7);
}

// E11: één ISO-correcte bron voor het weekbereik (zie lib/locatieTypes.js).
const getWeekRange = (year, week) => isoWeekRange(year, week);

const TrendIcon = ({ trend, className = 'w-4 h-4' }) => {
  if (trend === 'up') return <TrendingUp className={`${className} text-red-500`} />;
  if (trend === 'down') return <TrendingDown className={`${className} text-emerald-500`} />;
  return <Minus className={`${className} text-slate-400`} />;
};

const StatusBadge = ({ status }) => {
  const c = STATUS_CONFIG[status] || STATUS_CONFIG.OK;
  return (
    <span title={STATUS_TOOLTIP[status] || ''}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold ${c.bg} ${c.text} ${c.border} border cursor-help`}
      data-testid="status-badge">
      <span className={`w-2 h-2 rounded-full ${c.dot}`} />
      {status}
    </span>
  );
};

const ProgressBar = ({ pct }) => {
  const cl = Math.min(Math.max(pct, 0), 100);
  const color = cl >= 90 ? 'bg-emerald-500' : cl >= 75 ? 'bg-amber-500' : cl >= 50 ? 'bg-orange-500' : 'bg-red-500';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2.5 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-500 ${color}`} style={{ width: `${cl}%` }} />
      </div>
      <span className="text-sm font-semibold text-slate-600 w-12 text-right">{pct.toFixed(0)}%</span>
    </div>
  );
};

// ── Period Picker ────────────────────────────────────────────────────────────
const PeriodPicker = ({ period, selDate, setSelDate, selWeek, setSelWeek, selWeekYear, setSelWeekYear, selMonth, setSelMonth, selMonthYear, setSelMonthYear, selQuarter, setSelQuarter, selQuarterYear, setSelQuarterYear, selYear, setSelYear }) => {
  const now = new Date();
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="period-date-picker">
      {period === 'day' && (
        <div className="flex items-center gap-2">
          <CalendarDays className="w-4 h-4 text-slate-400" />
          <Input type="date" value={selDate} onChange={e => setSelDate(e.target.value)} className="w-44 h-8 text-sm" data-testid="day-picker" />
        </div>
      )}
      {period === 'week' && (
        <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg px-2 py-1">
          <button onClick={() => { let w = selWeek - 1, y = selWeekYear; if (w < 1) { y--; w = isoWekenInJaar(y); } setSelWeek(w); setSelWeekYear(y); }} className="p-1 hover:bg-slate-100 rounded" data-testid="week-prev"><ChevronDown className="w-3.5 h-3.5 rotate-90" /></button>
          <span className="px-2 text-sm font-medium text-slate-700 whitespace-nowrap" data-testid="week-label">Week {selWeek} ({getWeekRange(selWeekYear, selWeek)})</span>
          <button onClick={() => { let w = selWeek + 1, y = selWeekYear; if (w > isoWekenInJaar(y)) { y++; w = 1; } setSelWeek(w); setSelWeekYear(y); }} className="p-1 hover:bg-slate-100 rounded" data-testid="week-next"><ChevronDown className="w-3.5 h-3.5 -rotate-90" /></button>
        </div>
      )}
      {period === 'month' && (
        <div className="flex items-center gap-2">
          <select value={selMonth} onChange={e => setSelMonth(parseInt(e.target.value))} className="h-8 text-sm border border-slate-200 rounded-md px-2 bg-white" data-testid="month-picker">
            {MAAND_NAMEN.slice(1).map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
          </select>
          <select value={selMonthYear} onChange={e => setSelMonthYear(parseInt(e.target.value))} className="h-8 text-sm border border-slate-200 rounded-md px-2 bg-white" data-testid="month-year-picker">
            {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      )}
      {period === 'quarter' && (
        <div className="flex items-center gap-2">
          <div className="flex gap-1 bg-white border border-slate-200 rounded-lg p-0.5">
            {[1,2,3,4].map(q => (
              <button key={q} onClick={() => setSelQuarter(q)}
                className={`px-3 py-1 text-sm rounded-md font-medium transition-colors ${selQuarter === q ? 'bg-[#1f2548] text-white' : 'text-slate-600 hover:bg-slate-100'}`}
                data-testid={`quarter-q${q}`}>Q{q}</button>
            ))}
          </div>
          <select value={selQuarterYear} onChange={e => setSelQuarterYear(parseInt(e.target.value))} className="h-8 text-sm border border-slate-200 rounded-md px-2 bg-white">
            {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      )}
      {period === 'year' && (
        <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg px-2 py-1">
          <button onClick={() => setSelYear(y => y - 1)} className="p-1 hover:bg-slate-100 rounded" data-testid="year-prev"><ChevronDown className="w-3.5 h-3.5 rotate-90" /></button>
          <span className="px-3 text-sm font-semibold text-slate-700" data-testid="year-label">{selYear}</span>
          <button onClick={() => setSelYear(y => y + 1)} className="p-1 hover:bg-slate-100 rounded" disabled={selYear >= now.getFullYear()} data-testid="year-next"><ChevronDown className="w-3.5 h-3.5 -rotate-90" /></button>
        </div>
      )}
    </div>
  );
};

// ── Main Page ───────────────────────────────────────────────────────────────
const MijnPerformance = () => {
  const { selectedLocatie, selectedLocatieData } = useOutletContext();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');

  const now = new Date();
  const [selDate, setSelDate] = useState(now.toISOString().split('T')[0]);
  const [selWeek, setSelWeek] = useState(getISOWeek(now));
  const [selWeekYear, setSelWeekYear] = useState(isoWeekJaar(now));   // F3d: ISO-weekjaar
  const [selMonth, setSelMonth] = useState(now.getMonth() + 1);
  const [selMonthYear, setSelMonthYear] = useState(now.getFullYear());
  const [selQuarter, setSelQuarter] = useState(Math.ceil((now.getMonth() + 1) / 3));
  const [selQuarterYear, setSelQuarterYear] = useState(now.getFullYear());
  const [selYear, setSelYear] = useState(now.getFullYear());

  const fetchData = useCallback(async () => {
    if (!selectedLocatie) { setLoading(false); return; }
    setLoading(true);
    try {
      const token = getAccessToken();
      const params = new URLSearchParams({ period, locatie_id: selectedLocatie });
      if (period === 'day') { const d = new Date(selDate); params.set('jaar', d.getFullYear()); params.set('maand', d.getMonth() + 1); params.set('dag', d.getDate()); }
      else if (period === 'week') { params.set('jaar', selWeekYear); params.set('week', selWeek); params.set('maand', maandVanIsoWeek(selWeekYear, selWeek)); }
      else if (period === 'month') { params.set('jaar', selMonthYear); params.set('maand', selMonth); }
      else if (period === 'quarter') { params.set('jaar', selQuarterYear); params.set('maand', (selQuarter - 1) * 3 + 1); }
      else if (period === 'year') { params.set('jaar', selYear); }
      const res = await fetch(`${API}/performance/mijn-locatie?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.status === 404) { setData(null); return; }
      if (!res.ok) throw new Error();
      setData(await res.json());
    } catch { toast.error('Fout bij laden performance data'); }
    finally { setLoading(false); }
  }, [period, selectedLocatie, selDate, selWeek, selWeekYear, selMonth, selMonthYear, selQuarter, selQuarterYear, selYear]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const chartData = useMemo(() => {
    if (!data?.historie) return [];
    return data.historie.slice(-6).map(p => ({
      name: p.periode, Omzet: Math.round(p.omzet / 1000), Target: Math.round(p.target / 1000), 'PK%': p.pk_pct,
    }));
  }, [data]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-white shadow-lg border border-slate-200 rounded-lg p-3 text-xs">
        <p className="font-semibold text-slate-700 mb-1">{label}</p>
        {payload.map((p, i) => (
          <p key={i} style={{ color: p.color }}>{p.name}: {p.name === 'PK%' ? `${p.value}%` : `\u20AC ${p.value}K`}</p>
        ))}
      </div>
    );
  };

  // Determine attention points
  const aandachtspunten = useMemo(() => {
    if (!data?.huidig) return [];
    const h = data.huidig;
    const pts = [];
    const pkT = data.pk_target || 35;
    const bvT = data.bv_target || 7;
    if (h.pk_pct > pkT) pts.push({ type: 'pk', text: `PK% is ${h.pk_pct}% — boven target van ${pkT}%. Controleer personeelskosten.`, color: 'text-red-600' });
    if (h.bijverkoop_pct < bvT) pts.push({ type: 'bv', text: `Bijverkoop is ${h.bijverkoop_pct}% — onder target van ${bvT}%. Focus op upselling.`, color: 'text-amber-600' });
    if (h.voortgang_pct < 75) pts.push({ type: 'omzet', text: `Omzet voortgang ${h.voortgang_pct}% — achterblijvend. Extra focus op klantenwerving.`, color: 'text-amber-600' });
    return pts;
  }, [data]);

  if (loading) return <div className="flex items-center justify-center h-64" data-testid="performance-loading"><Loader2 className="w-8 h-8 animate-spin text-[#1f2548]" /></div>;
  if (!data) return <div className="text-center py-16 text-slate-400" data-testid="no-data">Geen performance data beschikbaar voor jouw locatie.</div>;

  const h = data.huidig || {};
  const pkT = data.pk_target || 35;
  const bvT = data.bv_target || 7;

  return (
    <div className="space-y-6 max-w-5xl mx-auto" data-testid="mijn-performance-page">
      {/* Header + Status */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-2xl font-bold text-[#1f2548]" data-testid="performance-title">Performance — {data.locatie_naam}</h1>
            <p className="text-sm text-slate-500">{data.period_label}</p>
          </div>
          <StatusBadge status={h.status || 'OK'} />
        </div>
        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1" data-testid="period-selector">
          {PERIODS.map(p => (
            <Button key={p.value} variant={period === p.value ? 'default' : 'ghost'} size="sm" onClick={() => setPeriod(p.value)} data-testid={`period-${p.value}`}
              className={period === p.value ? 'bg-[#1f2548] text-white hover:bg-[#2a3060] shadow-sm' : 'text-slate-600 hover:text-slate-900'}>
              {p.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Period picker */}
      <PeriodPicker period={period} selDate={selDate} setSelDate={setSelDate} selWeek={selWeek} setSelWeek={setSelWeek} selWeekYear={selWeekYear} setSelWeekYear={setSelWeekYear} selMonth={selMonth} setSelMonth={setSelMonth} selMonthYear={selMonthYear} setSelMonthYear={setSelMonthYear} selQuarter={selQuarter} setSelQuarter={setSelQuarter} selQuarterYear={selQuarterYear} setSelQuarterYear={setSelQuarterYear} selYear={selYear} setSelYear={setSelYear} />

      {/* DailyOps warning */}
      {data.dailyops_connected === false && (
        <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="dailyops-warning">
          <WifiOff className="w-5 h-5 text-amber-600 shrink-0" />
          <p className="text-sm text-amber-800">DailyOps is momenteel niet bereikbaar. Targets worden getoond, maar actuele data is niet beschikbaar.</p>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpi-cards">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-1.5 bg-[#1f2548]/10 rounded-md"><Euro className="w-4 h-4 text-[#1f2548]" /></div>
              <span className="text-xs font-medium text-slate-500 uppercase">Omzet vs Target</span>
            </div>
            <p className="text-xl font-bold text-slate-900" data-testid="kpi-omzet">{formatCurrency(h.omzet || 0)}</p>
            <ProgressBar pct={h.voortgang_pct || 0} />
            <p className="text-xs text-slate-400 mt-1" data-testid="performance-target-label">
              {h.heeft_target === false ? 'Geen target ingesteld' : `Target: ${formatCurrency(h.target || 0)}`}
            </p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-1.5 bg-[#1f2548]/10 rounded-md"><Users className="w-4 h-4 text-[#1f2548]" /></div>
              <span className="text-xs font-medium text-slate-500 uppercase">PK%</span>
            </div>
            <p className={`text-xl font-bold ${(h.pk_pct || 0) <= pkT ? 'text-emerald-600' : 'text-red-600'}`} data-testid="kpi-pk">{h.pk_pct || 0}%</p>
            <p className="text-xs text-slate-400">Target: {pkT}% {h.trend_pk && <TrendIcon trend={h.trend_pk} className="w-3 h-3 inline ml-1" />}</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-1.5 bg-[#1f2548]/10 rounded-md"><ShoppingBag className="w-4 h-4 text-[#1f2548]" /></div>
              <span className="text-xs font-medium text-slate-500 uppercase">Bijverkoop%</span>
            </div>
            <p className={`text-xl font-bold ${(h.bijverkoop_pct || 0) >= bvT ? 'text-emerald-600' : 'text-red-600'}`} data-testid="kpi-bv">{h.bijverkoop_pct || 0}%</p>
            <p className="text-xs text-slate-400">Target: {bvT}%</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-1.5 bg-[#1f2548]/10 rounded-md"><Target className="w-4 h-4 text-[#1f2548]" /></div>
              <span className="text-xs font-medium text-slate-500 uppercase">YTD Positie</span>
            </div>
            <p className={`text-xl font-bold ${(data.ytd?.achterstand || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`} data-testid="kpi-ytd">
              {(data.ytd?.achterstand || 0) >= 0 ? '+' : ''}{formatCurrency(data.ytd?.achterstand || 0)}
            </p>
            <p className="text-xs text-slate-400">Jaardoel: {formatCurrency(data.ytd?.jaardoel || 0)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Aandachtspunten */}
      {aandachtspunten.length > 0 && (
        <Card className="border-0 shadow-sm border-l-4 border-l-amber-400" data-testid="aandachtspunten">
          <CardContent className="p-4 space-y-2">
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              <span className="font-semibold text-sm text-slate-700">Aandacht nodig</span>
            </div>
            {aandachtspunten.map((a, i) => (
              <p key={i} className={`text-sm ${a.color}`}>{a.text}</p>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Charts */}
      {chartData.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="border-0 shadow-sm" data-testid="omzet-chart">
            <CardContent className="p-4">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Omzet trend (x1000)</h3>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={chartData} margin={{ top: 5, right: 5, bottom: 5, left: -10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Line type="monotone" dataKey="Omzet" stroke="#1f2548" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="Target" stroke="#ffc315" strokeWidth={2} strokeDasharray="5 5" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm" data-testid="pk-chart">
            <CardContent className="p-4">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">PK% trend</h3>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={chartData} margin={{ top: 5, right: 5, bottom: 5, left: -10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} domain={[0, 50]} />
                  <Tooltip content={<CustomTooltip />} />
                  <ReferenceLine y={pkT} stroke="#ef4444" strokeDasharray="3 3" label={{ value: `Target ${pkT}%`, fontSize: 10, fill: '#ef4444' }} />
                  <Line type="monotone" dataKey="PK%" stroke="#1f2548" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default MijnPerformance;
